import { useBundle } from '@amzn/react-arb-tools';
import { HEADING_TAG, ICON_NAME, SECTION_THEME, useDeviceSize } from '@amzn/sss-website-theme';

import { SegmentConfigProps } from 'src/components/pages/segment/types';
import { COMMON_BUNDLE_NAME, REDUCE_DEFORESTATION_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { getCreditsAssetRoot, getImageRoot, MEDIA_TYPE } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';

export function useReduceDeforestationConfig(): { isBundleLoading: boolean; config: SegmentConfigProps } {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [commonBundle, isCommonBundleLoading] = useBundle(COMMON_BUNDLE_NAME);
  const [reduceDeforestationBundle, isReduceDeforestationBundleLoading] = useBundle(REDUCE_DEFORESTATION_BUNDLE_NAME);

  const isBundleLoading = isCommonBundleLoading || isReduceDeforestationBundleLoading;

  // Display a plain image thumbnail on mobile and a video on desktop
  const deforestationSecondMediaAsset = isSmallDesktop
    ? {
        video: {
          videoSrc: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.VIDEO)}/credits-segments-deforestation-ourapproach-2.mp4`,
          thumbnail: {
            src: `${imageRoot}/credits-segments-deforestation-ourapproach-2.jpg`,
            alt: t(reduceDeforestationBundle, 'dynamicList_secondMedia_altText'),
          },
          ariaLabels: {
            play: t(commonBundle, 'playAnimation_ariaLabel'),
            pause: t(commonBundle, 'pauseAnimation_ariaLabel'),
          },
        },
        byLine: t(reduceDeforestationBundle, 'dynamicList_secondMedia_byLine'),
      }
    : {
        image: {
          src: `${imageRoot}/credits-segments-deforestation-ourapproach-2.jpg`,
          alt: t(reduceDeforestationBundle, 'dynamicList_secondMedia_altText'),
        },
        byLine: t(reduceDeforestationBundle, 'dynamicList_secondMedia_byLine'),
      };

  return {
    isBundleLoading,
    config: {
      heroAsset: {
        image: {
          src: `${imageRoot}/credits-segments-deforestation-hero-1.jpg`,
          alt: t(reduceDeforestationBundle, 'heroAsset_image_altText'),
        },
        title: t(reduceDeforestationBundle, 'heroAsset_title'),
      },

      dynamicList: {
        mediaProps: {
          mediaTitle: t(reduceDeforestationBundle, 'dynamicList_mediaTitle'),
          mediaTagline: t(reduceDeforestationBundle, 'dynamicList_mediaTagline'),
          media: [
            {
              image: {
                src: `${imageRoot}/credits-segments-deforestation-ourapproach-1.jpg`,
                alt: t(reduceDeforestationBundle, 'dynamicList_firstMedia_altText'),
              },
              byLine: t(reduceDeforestationBundle, 'dynamicList_firstMedia_byLine'),
            },
            deforestationSecondMediaAsset,
          ],
        },
        items: [
          {
            title: t(reduceDeforestationBundle, 'dynamicList_firstItem_title'),
            body: t(reduceDeforestationBundle, 'dynamicList_firstItem_body'),
          },
          {
            title: t(reduceDeforestationBundle, 'dynamicList_secondItem_title'),
            body: t(reduceDeforestationBundle, 'dynamicList_secondItem_body'),
          },
        ],
      },

      primaryBentoCards: {
        firstCard: `${imageRoot}/credits-segments-deforestation-ourquality-1.jpg`,
        secondCard: `${imageRoot}/credits-segments-deforestation-ourquality-3.jpg`,

        bentoCardProps: {
          label: t(reduceDeforestationBundle, 'primaryBentoCards_label'),
          title: t(reduceDeforestationBundle, 'primaryBentoCards_title'),
          titleSize: HEADING_TAG.H4,
          description: t(reduceDeforestationBundle, 'primaryBentoCards_description'),
          tooltipAttributes: [
            {
              cta: {
                url: t(reduceDeforestationBundle, 'primaryBentoCards_tooltip_linkUrl'),
                label: t(reduceDeforestationBundle, 'primaryBentoCards_tooltip_linkLabel'),
                icon: ICON_NAME.CHEVRON_TOP_RIGHT,
              },
              description: t(reduceDeforestationBundle, 'primaryBentoCards_tooltip_description'),
            },
          ],
          tooltipTheme: SECTION_THEME.Light,
          firstCard: {
            label: t(reduceDeforestationBundle, 'primaryBentoCards_firstCard_label'),
            title: t(reduceDeforestationBundle, 'primaryBentoCards_firstCard_title'),
            description: t(reduceDeforestationBundle, 'primaryBentoCards_firstCard_description'),
          },
          secondCard: {
            src: `${imageRoot}/credits-segments-deforestation-thechallenge-3.jpg`,
            alt: t(reduceDeforestationBundle, 'primaryBentoCards_secondCard_altText'),
          },
        },
      },

      secondaryBentoCards: {
        firstCard: `${imageRoot}/credits-segments-deforestation-ourquality-1.jpg`,
        firstCardModal: `${imageRoot}/credits-segments-deforestation-ourquality-2.jpg`,
        secondCard: `${imageRoot}/credits-segments-deforestation-ourquality-3.jpg`,
        secondCardModal: `${imageRoot}/credits-segments-deforestation-ourquality-4.jpg`,
        bentoCardProps: {
          label: t(reduceDeforestationBundle, 'secondaryBentoCards_label'),
          title: t(reduceDeforestationBundle, 'secondaryBentoCards_title'),
          titleSize: HEADING_TAG.H4,
          description: t(reduceDeforestationBundle, 'secondaryBentoCards_description'),
          tooltipAttributes: [
            {
              description: t(reduceDeforestationBundle, 'secondaryBentoCards_tooltip_description'),
            },
          ],
          tooltipTheme: SECTION_THEME.Light,
          firstCard: {
            label: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_label'),
            title: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-deforestation-ourquality-1.jpg`,
              alt: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_altText'),
            },
            modal: {
              label: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_label'),
              title: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_title'),
              image: {
                src: `${imageRoot}/credits-segments-deforestation-ourquality-2.jpg`,
                alt: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_modal_altText'),
                imageTaglineOne: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_modal_taglineOne'),
                imageTaglineTwo: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_modal_taglineTwo'),
              },
              modalContent: t(reduceDeforestationBundle, 'secondaryBentoCards_firstCard_modal_content'),
              link: undefined,
            },
          },
          secondCard: {
            label: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_label'),
            title: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-deforestation-ourquality-3.jpg`,
              alt: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_altText'),
            },
            modal: {
              image: {
                src: `${imageRoot}/credits-segments-deforestation-ourquality-4.jpg`,
                alt: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_modal_altText'),
              },
              label: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_label'),
              title: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_title'),
              modalContent: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_modal_content'),
              link: {
                url: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_modal_linkUrl'),
                label: t(reduceDeforestationBundle, 'secondaryBentoCards_secondCard_modal_linkLabel'),
                icon: ICON_NAME.INFO_OUTLINED,
              },
            },
          },
        },
      },
    },
  };
}
